import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DropContainerComponent from "./subcomponents/DropContainerComponent";
import EditPropertiesComponent from "./subcomponents/EditPropertiesComponent";
import { isMobile as libIsMobile, isTablet as libIsTablet } from "react-device-detect";
import LeftSidebar from "./LeftSidebar";
import useFormBuilder from "./hooks/useFormBuilder";
import useFormPreview from "./hooks/useFormPreview";
import { Publish, RemoveRedEye } from "@mui/icons-material";
import { FormItemTypes } from "../../utils/formBuilderUtils";
import FormPreview from "./subcomponents/FormPreview";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo_ke.jpeg";


let isMobile;
if (process.env.NODE_ENV === "localhost") {
  isMobile = window.innerWidth < 1024;
} else {
  isMobile = libIsMobile || libIsTablet || window.innerWidth < 1024;
}

const useStyles = makeStyles(() => ({
  textField: {
    minWidth: "100%",
    maxWidth: "100%",
  },
  sidebarHeight: {
    height: "calc(100vh - 63px)",
    overflowY: "auto",
  },
}));

const FormBuilder = (props) => {
  const {
    handleItemAdded,
    saveForm,
    deleteContainer,
    deleteControl,
    editContainerProperties,
    editControlProperties,
    moveControl,
    moveControlFromSide,
    publishForm,
    selectControl,
    selectedTemplate,
    formLayoutComponents,
    selectedControl,
  } = useFormBuilder({ template: props.template });

  const { showPreview, openPreviewDrawer, closePreviewDrawer } = useFormPreview();
  const { classes } = useStyles();
  const navigate = useNavigate();

  // Modal state
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isPublishAction, setPublishAction] = useState(false);

  // Handlers for Save and Publish with modal
  const handleSave = () => {
    saveForm();
    setModalMessage("Form saved successfully!");
    setModalOpen(true);
    setPublishAction(false);
  };

  const handlePublish = () => {
    publishForm();
    setModalMessage("Form published successfully!");
    setModalOpen(true);
    setPublishAction(true);
  };

  // Close modal handler
  const handleCloseModal = () => {
    setModalOpen(false);
    if (isPublishAction) {
      navigate("/main-layout");
    }
  };

  return (
    <>
      {!isMobile ? (
        <DndProvider backend={HTML5Backend}>
          <div className="wrapper">
            <div className="row">
              {/* Left Sidebar */}
              <div
                className={`sidebar col-lg-3`}
                style={{
                  paddingLeft: 30,
                  height: "calc(100vh - 63px)",
                  overflowY: "auto",
                }}
              >
                <div className="container">
                  {selectedTemplate?.logo &&
                    <div id="logo" className="d-flex justify-content-center">
                      <a
                        href={"/main-layout"}
                        className="logo vcenter"
                        data-src-dark="/img/logo_ke.png"
                        style={{ fontFamily: "Cassannet", fontSize: "16px" }}
                      >
                        <img
                          src={`${process.env.REACT_APP_IMG_URL}/${selectedTemplate?.logo}`}
                          alt="Form Dynamic"
                          style={{ marginTop: "7px", width: "150px", height: "100px", display: "inline-block", borderRadius: "50%" }}
                          className={"p-l-20 p-r-15"}
                        />
                      </a>
                    </div>}

                  <LeftSidebar
                    handleItemAdded={handleItemAdded}
                    formLayoutComponents={formLayoutComponents}
                  />
                </div>
              </div>

              {/* Main Form Layout */}
              <div className="col-lg-6">
                <div className="container p-20 h-100">
                  {/* Form Header */}
                  <div className="row mb-5">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-between">
                        <h4 className="mb-0">{selectedTemplate?.formName}</h4>
                        <div className="action-buttons d-flex">
                          <Button onClick={() => navigate("/main-layout")} className="mx-2"
                            style={{ height: "40px" }}
                          >
                            Cancel
                          </Button>
                          <Button onClick={handleSave} className="mx-2"
                            style={{ height: "40px" }}
                          >
                            Save
                          </Button>
                          <Button
                            className="mx-2"
                            size="small"
                            style={{ height: "40px" }}
                            variant="outlined"
                            onClick={openPreviewDrawer}
                            endIcon={<RemoveRedEye />}
                          >
                            Preview
                          </Button>
                          <Button
                            className="mx-2"
                            size="small"
                            style={{ height: "40px" }}
                            color="primary"
                            variant="contained"
                            onClick={handlePublish}
                            endIcon={<Publish />}
                          >
                            Publish
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Containers */}
                  <div
                    className="p-20"
                    style={{
                      overflowY: "auto",
                      height: "calc(100vh - 180px)",
                    }}
                  >
                    <div className="row mb-5">
                      {formLayoutComponents.map((layout, ind) => (
                        <DropContainerComponent
                          key={layout.container.id}
                          index={ind}
                          layout={layout.container}
                          selectedControl={selectedControl}
                          childrenComponents={layout.children}
                          deleteContainer={deleteContainer}
                          deleteControl={deleteControl}
                          selectControl={selectControl}
                          accept={FormItemTypes.CONTROL}
                          moveControl={moveControl}
                        />
                      ))}
                    </div>
                    <div className="row mb-5">
                      <DropContainerComponent
                        accept={FormItemTypes.CONTAINER}
                        name={"Parent Component"}
                        handleItemAdded={handleItemAdded}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <div
                className={`sidebar col-lg-3 `}
                style={{
                  height: "calc(100vh - 63px)",
                  overflowY: "auto",
                }}
              >
                <div className="container">
                  <EditPropertiesComponent
                    selectedControl={selectedControl}
                    selectControl={selectControl}
                    formLayoutComponents={formLayoutComponents}
                    moveControlFromSide={moveControlFromSide}
                    editContainerProperties={editContainerProperties}
                    editControlProperties={editControlProperties}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Preview */}
          <FormPreview
            screenType="mobile"
            showPreview={showPreview}
            formLayoutComponents={formLayoutComponents}
            closePreviewDrawer={closePreviewDrawer}
          />
        </DndProvider>
      ) : (
        <div className="wrapper mt-5">
          <p className="text-center">
            Form Builder is only supported on desktop devices for now. Please switch to a desktop
            device.
          </p>
        </div>
      )}

      {/* Success Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{modalMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormBuilder;
